import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  AcceptPlanOfferResponse,
  AccountsService,
  PaidPricingTableKey,
  paidPricingTableKeySchema,
  PaymentInfoCommon,
  PricingTable,
} from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { getCookie } from 'base/api_host';
import { UnreachableError } from 'base/helpers';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { InvisibleForm, RedErrorText, useSubmit } from 'base/ui/form';
import { IconClear, IconCoupon } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { LoadingDots } from 'base/ui/loading_dots';
import { DividerLine } from 'base/ui/panel';
import { Toggle } from 'base/ui/toggle';
import { COLOR_TOKENS } from 'base/ui/tokens';
import { Tooltip } from 'base/ui/tooltip';
import {
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  TextTiny,
  TextTinyLight,
  Title1,
  Title6,
} from 'base/ui/typography';
import { useLiveRef } from 'base/useLiveRef';
import { useFetchData } from 'base/use_fetch_data';
import React from 'react';
import iconGiftUrl from '../dashboard/icon-gift.file.svg';
import styles from './accept_plan_offer.css';
import amexCardImage from './cards/amex.file.svg';
import jcbCardImage from './cards/jcb.file.svg';
import mastercardCardImage from './cards/mastercard.file.svg';
import unionpayCardImage from './cards/unionpay.file.svg';
import visaCardImage from './cards/visa.file.svg';
import { formatCentAmount, OpenStripeBillingButton } from './plan_buttons';

export type AcceptPlanDialogUrlState =
  | {
      planKey: PaidPricingTableKey;
      endTrialNow?: boolean;
      trialPeriodDays?: number;
      currentPayment?: number;
      ongoingPayment?: number;
      couponCode?: string;
      setupIntentId?: string;
      paymentIntentId?: string;
    }
  | undefined;

export function parseAcceptPlanDialogUrlState(
  params: URLSearchParams,
): AcceptPlanDialogUrlState {
  const planKeyRaw = params.get('>pk');
  const planKey = paidPricingTableKeySchema.safeParse(planKeyRaw);
  if (!planKey.success) {
    return;
  }
  const endTrialNow = parseBooleanParam(params.get('>etn'));
  const trialPeriodDays = nanToUndefined(parseInt(params.get('>tp') || ''));
  const currentPayment = nanToUndefined(parseInt(params.get('>cp') || ''));
  const ongoingPayment = nanToUndefined(parseInt(params.get('>op') || ''));
  const couponCode = params.get('>coupon') || undefined;
  return {
    planKey: planKey.data,
    endTrialNow,
    trialPeriodDays,
    currentPayment,
    ongoingPayment,
    couponCode,
    // stripe adds these parameter on redirect:
    setupIntentId: params.get('setup_intent') || undefined,
    paymentIntentId: params.get('payment_intent') || undefined,
  };
}

function parseBooleanParam(s: string | null): boolean | undefined {
  if (s === 'true') return true;
  if (s === 'false') return false;
  return undefined;
}

function nanToUndefined(n: number): number | undefined {
  return isNaN(n) ? undefined : n;
}

export function serializeAcceptPlanDialogUrlState(
  state: AcceptPlanDialogUrlState,
  params: URLSearchParams,
) {
  if (!state) return;
  params.set('>pk', state.planKey);
  if (state.endTrialNow) {
    params.set('>etn', 'true');
  }
  if (state.trialPeriodDays != null) {
    params.set('>tp', state.trialPeriodDays.toString());
  }
  if (state.currentPayment != null) {
    params.set('>cp', state.currentPayment.toString());
  }
  if (state.ongoingPayment != null) {
    params.set('>op', state.ongoingPayment.toString());
  }
  if (state.couponCode) {
    params.set('>coupon', state.couponCode);
  }
  if (state.setupIntentId) {
    params.set('setup_intent', state.setupIntentId);
  }
  if (state.paymentIntentId) {
    params.set('payment_intent', state.paymentIntentId);
  }
}
const PLAN_NAME_BY_KEY = {
  starterMonthly: 'Starter Membership',
  starterYearly: 'Starter Membership',
  proMonthly: 'Pro Membership',
  proYearly: 'Pro Membership',
  teamMonthly: 'Team Membership',
  teamYearly: 'Team Membership',
} satisfies Record<PaidPricingTableKey, string>;

export function AcceptPlanOfferDialog({
  urlState,
  setUrlState,
  accountsService,
  accountStore,
  onClose,
  redirectOnSuccess,
  canChangePeriod = false,
}: {
  urlState: Exclude<AcceptPlanDialogUrlState, undefined>;
  setUrlState: (state: AcceptPlanDialogUrlState | undefined) => void;
  accountsService: AccountsService;
  accountStore: AccountStore;
  onClose: () => void;
  redirectOnSuccess: (
    planChosen: keyof PricingTable | undefined,
    hasCoupon: boolean,
    urlState?: AcceptPlanDialogUrlState,
  ) => void;
  canChangePeriod?: boolean;
}) {
  const [state, setState] = React.useState<
    | { type: 'none' }
    | { type: 'loading'; previousRes?: AcceptPlanOfferResponse }
    | { type: 'error'; error: Error }
    | { type: 'loaded'; res: AcceptPlanOfferResponse }
  >({ type: 'none' });
  const credit = useFetchData(() => accountsService.getBalance());

  const [paymentInProgress, setPaymentInProgress] = React.useState(0);
  const trackPaymentInProgress = React.useCallback(
    async (fn: () => Promise<void>) => {
      setPaymentInProgress((n) => n + 1);
      try {
        await fn();
      } finally {
        setPaymentInProgress((n) => n - 1);
      }
    },
    [setPaymentInProgress],
  );

  function reload(opts: { confirmed?: boolean }) {
    if (state.type === 'loading') return;
    setState({
      type: 'loading',
      previousRes: state.type === 'loaded' ? state.res : undefined,
    });
    let rewardfulReferralId: string | undefined = undefined;
    if (window?.Rewardful?.referral) {
      rewardfulReferralId = window.Rewardful.referral;
    }
    accountsService
      .acceptPlanOffer({
        planKey: urlState.planKey,
        redirectUrl: window.location.href,
        endTrialNow: urlState.endTrialNow,
        confirmTrialPeriodDays: opts.confirmed
          ? urlState.trialPeriodDays
          : undefined,
        confirmCurrentPayment: opts.confirmed
          ? urlState.currentPayment
          : undefined,
        confirmOngoingPayment: opts.confirmed
          ? urlState.ongoingPayment
          : undefined,
        setupIntentId: urlState.setupIntentId,
        couponCode: urlState.couponCode,
        fbc: getCookie('fbclid'),
        fbp: getCookie('_fbp'),
        gclid: getCookie('gclid'),
        rewardfulReferralId,
      })
      .then((res) => {
        setState({ type: 'loaded', res });
        if (res.status === 'success') {
          accountStore.get(true).finally(() => {
            redirectOnSuccess(
              urlState.planKey,
              urlState.couponCode != null,
              urlState,
            );
          });
        }
        if (res.status === 'alreadyOnPlan' && urlState.paymentIntentId) {
          // Redirected back after completing 3D secure (on a failed payment - post sub creation)
          // If 3DS failed, the backend would've given us an error.
          redirectOnSuccess(
            urlState.planKey,
            urlState.couponCode != null,
            urlState,
          );
        }
        // Save the offer that the user saw to the URL, so that
        // we can confirm it when the user returns from the
        // stripe redirect.
        if (
          res.status === 'requiresPaymentMethod' ||
          res.status === 'requiresPaymentConfirmation'
        ) {
          if (
            urlState.currentPayment !== res.currentPayment.cents ||
            urlState.ongoingPayment !== res.ongoingPayment.cents ||
            urlState.trialPeriodDays !== res.trialPeriodDays
          ) {
            setUrlState({
              ...urlState,
              currentPayment: res.currentPayment.cents,
              ongoingPayment: res.ongoingPayment.cents,
              trialPeriodDays: res.trialPeriodDays,
            });
          }
        }
      })
      .catch((error) => {
        setState({ type: 'error', error });
      });
  }

  React.useEffect(() => {
    if (state.type === 'loading') return;
    // Stripe adds the setup intent on redirect.
    // So the user has clicked a button if the setup intent is present.
    reload({
      confirmed: state.type == 'none' && urlState.setupIntentId != null,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState.couponCode]);

  // Reload when the plan key changes (i.e. annual toggle)
  const yearly = urlState.planKey.endsWith('Yearly');
  const reloadRef = useLiveRef(reload);
  const stateRef = useLiveRef(state);
  React.useEffect(() => {
    if (stateRef.current.type !== 'loaded') {
      return;
    }
    yearly;
    reloadRef.current({});
  }, [reloadRef, stateRef, yearly]);

  const saveCouponCode = (couponCode: string | undefined) => {
    setUrlState({ ...urlState, couponCode });
  };
  const res =
    state.type === 'loaded'
      ? state.res
      : state.type === 'loading'
      ? state.previousRes
      : undefined;
  const couponCodeLink = (
    <CuponCodeField
      saved={urlState.couponCode}
      onSave={saveCouponCode}
      coupon={
        res?.status === 'requiresPaymentConfirmation' ||
        res?.status === 'requiresPaymentMethod'
          ? res.coupon
          : undefined
      }
      couponError={
        res?.status === 'requiresPaymentConfirmation' ||
        res?.status === 'requiresPaymentMethod'
          ? res?.couponError || ''
          : undefined
      }
      loading={state.type === 'loading'}
    />
  );

  const loading = state.type === 'loading' || credit.loading;
  const canClose =
    state.type === 'error' ||
    (state.type === 'loaded' && state.res.status !== 'success');
  const closeButton = (
    <Button
      variant="secondary"
      label="Cancel"
      size="large"
      onClick={onClose}
      disabled={!canClose}
    />
  );

  const annualDiscountToggle = (
    <div>
      <Toggle
        label="Yearly (30% discount)"
        onChange={() => {
          setUrlState({
            ...urlState,
            planKey:
              urlState.planKey === 'teamMonthly'
                ? 'teamYearly'
                : urlState.planKey === 'teamYearly'
                ? 'teamMonthly'
                : urlState.planKey === 'proMonthly'
                ? 'proYearly'
                : urlState.planKey === 'proYearly'
                ? 'proMonthly'
                : urlState.planKey === 'starterMonthly'
                ? 'starterYearly'
                : 'starterMonthly',
          });
        }}
        value={urlState.planKey.endsWith('Yearly')}
        color="green"
        disabledReason={
          canChangePeriod
            ? undefined
            : 'Please contact support@relume.io to change your billing interval'
        }
      />
    </div>
  );

  let content;
  let title = PLAN_NAME_BY_KEY[urlState.planKey];
  if (state.type === 'error') {
    content = <RedErrorText error={state.error} />;
  } else if (res) {
    if (res.status === 'success') {
      content = <SuccessContent />;
    } else if (res.status === 'requiresPaymentMethod') {
      content = (
        <>
          {Boolean(res.trialPeriodDays && res.trialPeriodDays > 0) && (
            <div className={styles.trialBanner}>
              <Rows spacing={4}>
                <TextSmall>
                  <strong>{res.trialPeriodDays}-Day free trial</strong>
                </TextSmall>
                <TextTiny>
                  Start your free trial now. Cancel within {res.trialPeriodDays}{' '}
                  days to avoid charges.
                </TextTiny>
              </Rows>
            </div>
          )}
          {annualDiscountToggle}
          <StripeProvider {...res}>
            <LoadingContainer loading={loading}>
              <Rows spacing={16}>
                <PaymentInfo {...res} />
                <CreditInfo balance={credit.data?.balance || 0} />
              </Rows>
            </LoadingContainer>
            <DividerLine />
            <PaymentForm
              couponCodeLink={couponCodeLink}
              closeButton={closeButton}
              trackPaymentInProgress={trackPaymentInProgress}
            />
            <RedErrorText error={res.setupIntentError} />
            {res.confirmationFailed && <ConfirmationFailedMessage />}
          </StripeProvider>
        </>
      );
    } else if (res.status === 'requiresPaymentConfirmation') {
      content = (
        <>
          {annualDiscountToggle}
          <LoadingContainer loading={loading}>
            <Rows spacing={16}>
              <PaymentInfo {...res} />
              <CreditInfo balance={credit.data?.balance || 0} />
            </Rows>
          </LoadingContainer>
          <DividerLine />
          <PaymentMethodPreview
            {...res.paymentMethod}
            accountService={accountsService}
          />
          {couponCodeLink}
          {res.confirmationFailed && <ConfirmationFailedMessage />}
          <DialogButtons>
            {closeButton}
            <Button
              size="large"
              label="Confirm"
              variant="primary"
              onClick={() => reload({ confirmed: true })}
              loading={loading}
            />
          </DialogButtons>
        </>
      );
    } else if (res.status === 'alreadyOnPlan') {
      content = (
        <>
          <TextRegularLight>You're already on this plan!</TextRegularLight>
          {couponCodeLink}
          <DialogButtons>
            {closeButton}
            <Button
              size="large"
              label="Confirm"
              variant="primary"
              onClick={() => redirectOnSuccess(undefined, false)}
              loading={loading}
            />
          </DialogButtons>
        </>
      );
    } else if (res.status === 'requiresPaymentIntentAction') {
      content = (
        <StripeProvider {...res}>
          <PaymentIntentAction {...res} />
        </StripeProvider>
      );
    } else if (res.status === 'generalPaymentError') {
      content = (
        <>
          <TextRegularLight>
            Additional information is required to complete your payment.
          </TextRegularLight>
          <DialogButtons>
            <Button
              size="large"
              label="Fix payment"
              variant="primary"
              href={res.invoiceUrl}
              loading={loading}
            />
          </DialogButtons>
        </>
      );
    } else {
      throw new UnreachableError(res);
    }
  } else if (state.type === 'loaded') {
    throw new Error('Unexpected state (loaded but res is undefined)');
  } else if (state.type === 'loading' || state.type === 'none') {
    content = (
      <>
        <LoadingDots aspectRatio={2 / 1} />
        <DialogButtons>
          {closeButton}
          <Button
            variant="primary"
            size="large"
            label="Confirm"
            disabled
            loading
          />
        </DialogButtons>
      </>
    );
  } else {
    throw new UnreachableError(state);
  }

  return (
    <Dialog
      title={title}
      onClose={canClose ? onClose : undefined}
      // Stripe might display a 3D secure dialog during the payment.
      // If we've got a focus lock, the user can't type into Stripe's dialog.
      disableFocusLock={paymentInProgress > 0}
    >
      <Rows spacing={16}>{content}</Rows>
    </Dialog>
  );
}

function ConfirmationFailedMessage() {
  return (
    <RedErrorText error="We found you a better deal - no payment was made.  Please click again to confirm." />
  );
}

const CARD_BRAND_IMAGES: Record<string, string | undefined> = {
  visa: visaCardImage,
  mastercard: mastercardCardImage,
  amex: amexCardImage,
  jcb: jcbCardImage,
  unionpay: unionpayCardImage,
};

function PaymentMethodPreview({
  brand,
  last4,
  accountService,
}: {
  brand: string;
  last4: string;
  accountService: AccountsService;
}) {
  const brandTitleCase = brand[0].toUpperCase() + brand.slice(1);
  const brandImage = CARD_BRAND_IMAGES[brand];

  return (
    <div className={styles.paymentMethodPreview}>
      <Rows spacing={8}>
        <TextSmallLight color="grey">Payment Method</TextSmallLight>
        <Inline spacing={8} yalign="center">
          {brandImage && (
            <img
              className={styles.paymentMethodBrand}
              src={brandImage}
              alt={`${brandTitleCase} logo`}
            />
          )}
          <TextRegularLight>
            {brandTitleCase} •••• {last4}
          </TextRegularLight>
        </Inline>
      </Rows>
      <OpenStripeBillingButton
        accountService={accountService}
        label="Change"
        variant="subtle"
      />
    </div>
  );
}

function CuponCodeField({
  saved,
  coupon,
  couponError,
  onSave,
  loading,
}: {
  saved: string | undefined;
  coupon:
    | {
        name: string;
        explanation: string;
        variesWithTime: boolean;
      }
    | null
    | undefined;
  couponError: string | undefined;
  onSave: (couponCode: string | undefined) => void;
  loading: boolean;
}) {
  const [editing, setEditing] = React.useState(false);

  const removeCoupon = () => {
    if (saved) {
      onSave(undefined);
    } else {
      // The user hasn't entered a coupon code.  Their coupon was added by the backend (e.g. previous
      // coupon or Flux Student).
      // The user would get a worse deal by removing the coupon, so we only let them add a new coupon.
      setEditing(true);
    }
  };

  if (loading && saved) {
    return (
      <Rows spacing={8}>
        <TextSmallLight color="grey">Coupon code</TextSmallLight>
        <Inline spacing={8} yalign="center">
          <RemoveCouponBadge couponCode={saved} removeCoupon={removeCoupon} />
          <TextTinyLight color="grey">Loading...</TextTinyLight>
        </Inline>
      </Rows>
    );
  }
  if (editing || couponError) {
    return (
      <AddCouponCodeField
        key={`${saved} ${couponError}`}
        initialValue={saved}
        initialError={couponError}
        onSave={(v) => {
          onSave(v);
          setEditing(false);
        }}
        loading={loading}
      />
    );
  }
  if (coupon) {
    return (
      <Rows spacing={8}>
        <TextSmallLight color="grey">Coupon code</TextSmallLight>
        <Inline spacing={8} yalign="center">
          <RemoveCouponBadge
            couponCode={coupon.name}
            removeCoupon={removeCoupon}
          />
          <TextTinyLight color="grey">{coupon.explanation}</TextTinyLight>
        </Inline>
      </Rows>
    );
  }
  return (
    <Link subtle onClick={() => setEditing(true)}>
      <Inline spacing={8} yalign="center">
        <div className={styles.inlineCouponIcon}>
          <IconCoupon />
        </div>
        <TextSmall color="grey">I have a coupon code</TextSmall>
      </Inline>
    </Link>
  );
}

function RemoveCouponBadge({
  couponCode,
  removeCoupon,
}: {
  couponCode: string | undefined;
  removeCoupon: () => void;
}) {
  return (
    <div className={styles.couponBadge} onClick={removeCoupon}>
      <Inline spacing={4}>
        <div className={styles.tinyInlineCouponIcon}>
          <IconCoupon />
        </div>
        <TextTiny>{couponCode}</TextTiny>
        <div />
        <div className={styles.tinyInlineCouponIcon}>
          <IconClear />
        </div>
      </Inline>
    </div>
  );
}

function AddCouponCodeField({
  initialValue,
  initialError,
  onSave,
  loading,
}: {
  initialValue: string | undefined;
  initialError: string | undefined;
  onSave: (couponCode: string | undefined) => void;
  loading: boolean;
}) {
  const [value, setValue] = React.useState(initialValue);
  // We can't use a real form, as that'd nest forms.
  function submit() {
    onSave(value?.trim().length ? value.trim() : undefined);
  }
  return (
    <Rows spacing={8}>
      <TextSmallLight color="grey">Coupon code</TextSmallLight>
      <div className={styles.couponInputAndButton}>
        <Rows spacing={4}>
          <FormInput
            value={value || ''}
            onChange={setValue}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                submit();
              }
            }}
            type="text"
            size="large"
            autoFocus
          />
          {initialError && value === initialValue && (
            <TextTinyLight color="red">{initialError}</TextTinyLight>
          )}
        </Rows>
        <Button
          onClick={submit}
          variant="primary"
          size="large"
          label="Apply"
          loading={loading}
        />
      </div>
    </Rows>
  );
}

function SuccessContent() {
  return (
    <>
      <div />
      <TextRegularLight align="center">Success!</TextRegularLight>
      <TextRegularLight align="center" color="grey">
        You are being redirected...
      </TextRegularLight>
      <div />
    </>
  );
}

const CreditInfo = ({ balance }: { balance: number }) => (
  <>
    {balance > 0 && (
      <div className={styles.creditContainer}>
        <Title6 color="gradient">{`+$${balance}`}</Title6>
        <TextSmallLight color="grey">
          Your referral credit applies to your next payment
        </TextSmallLight>
      </div>
    )}
  </>
);

export function PaymentInfo({
  currentPayment,
  currentPaymentDateHuman,
  ongoingPayment,
  ongoingPaymentDateHuman,
  ongoingInterval,
  coupon,
}: PaymentInfoCommon) {
  return (
    <Rows spacing={16}>
      {coupon?.metadata &&
        (coupon.metadata.title || coupon.metadata.description) && (
          <div className={styles.couponContainer}>
            <img src={iconGiftUrl} className={styles.giftImage} />
            <Rows spacing={0}>
              {coupon.metadata.title && (
                <TextSmall>{coupon.metadata.title}</TextSmall>
              )}
              {coupon.metadata.description && (
                <TextSmallLight>{coupon.metadata.description}</TextSmallLight>
              )}
            </Rows>
          </div>
        )}
      <Inline spacing={8} yalign="end" xalign="space-between">
        <Rows spacing={4} fixedWidth={false}>
          <TextSmallLight color="grey">
            Due {currentPaymentDateHuman}
          </TextSmallLight>
          <Title1 tag="div">
            {currentPayment.totalDiscount > 0 && (
              <span className={styles.discountPriceCurrentDueDate}>
                <DiscountOldPrice price={currentPayment} />{' '}
              </span>
            )}
            {formatCentAmount(currentPayment.cents)}

            <TextRegularLight tag="span">
              {' '}
              {currentPayment.currency.toUpperCase()}
            </TextRegularLight>
          </Title1>
        </Rows>
        <Rows spacing={0} fixedWidth={false}>
          <TextRegularLight>
            Then{' '}
            {ongoingPayment.totalDiscount > 0 && (
              <>
                <DiscountOldPrice price={ongoingPayment} />{' '}
              </>
            )}
            {`${formatCentAmount(
              ongoingPayment.cents,
            )} ${ongoingPayment.currency.toUpperCase()} per ${ongoingInterval}`}
          </TextRegularLight>
          {ongoingPayment.cents > 0 && (
            <TextRegularLight>
              Next payment {ongoingPaymentDateHuman}
            </TextRegularLight>
          )}
          {coupon?.variesWithTime && (
            <TextRegularLight>
              Payment amount will change once coupon expires
            </TextRegularLight>
          )}
        </Rows>
      </Inline>
    </Rows>
  );
}

function DiscountOldPrice({
  price: { cents, totalDiscount },
}: {
  price: PaymentInfoCommon['ongoingPayment'];
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  return (
    <s className={styles.discountOldPrice} ref={ref}>
      {formatCentAmount(cents + totalDiscount)}
      <Tooltip
        anchorRef={ref}
        placement="vertical"
        label={`You save ${formatCentAmount(totalDiscount)} - great deal!`}
      />
    </s>
  );
}

function StripeProvider({
  stripePublicKey,
  clientSecret,
  children,
}: {
  stripePublicKey: string;
  clientSecret: string;
  children: React.ReactNode;
}) {
  const [stripePromise] = React.useState(() => loadStripe(stripePublicKey));
  return (
    <Elements
      stripe={stripePromise}
      options={{ ...STRIPE_ELEMENTS_STYLE, clientSecret }}
    >
      {children}
    </Elements>
  );
}

function PaymentIntentAction({ clientSecret }: { clientSecret: string }) {
  const stripe = useStripe();
  const [loading, error, handleSubmit] = useSubmit(async () => {
    if (!stripe) {
      throw new Error('Please wait for stripe to load');
    }
    const result = await stripe.confirmPayment({
      clientSecret,
      confirmParams: { return_url: window.location.href },
      redirect: 'always',
    });
    if (result.error) {
      let msg =
        result.error.message || `Unknown stripe error: ${result.error.type}`;
      if (!msg.endsWith('.')) msg += '.';
      msg += ' Please contact support@relume.io.';
      throw new Error(msg);
    }
  });

  // submit on mount (but after stripe has loaded)
  React.useEffect(() => {
    if (stripe) handleSubmit();
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  return (
    <InvisibleForm onSubmit={handleSubmit} loading={loading}>
      <TextRegularLight>
        You need to confirm this payment with your bank.
      </TextRegularLight>
      <RedErrorText error={error} />
      <DialogButtons>
        <Button
          variant="primary"
          type="submit"
          size="large"
          disabled={error != null}
          loading={loading}
          label="Confirm"
        />
      </DialogButtons>
    </InvisibleForm>
  );
}

function PaymentForm({
  couponCodeLink,
  closeButton,
  trackPaymentInProgress,
}: {
  couponCodeLink: React.ReactNode;
  closeButton: React.ReactNode;
  trackPaymentInProgress: (fn: () => Promise<void>) => Promise<void>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, error, handleSubmit] = useSubmit(async () => {
    await trackPaymentInProgress(async () => {
      if (!elements || !stripe) {
        throw new Error('Please wait for stripe to load');
      }
      const result = await stripe.confirmSetup({
        redirect: 'always',
        elements,
        confirmParams: { return_url: window.location.href },
      });
      if (result.error) {
        throw new Error(
          result.error.message || `Unknown stripe error: ${result.error.type}`,
        );
      }
    });
    // They've been redirected.  Keep this page in the loading state forever
    await new Promise(() => {});
  });
  return (
    <InvisibleForm
      onSubmit={handleSubmit}
      loading={loading}
      disabled={elements == null}
    >
      <PaymentElement />
      {couponCodeLink}
      <RedErrorText error={error} />
      <DialogButtons>
        {closeButton}
        <Button
          variant="primary"
          type="submit"
          size="large"
          loading={loading}
          label="Confirm"
        />
      </DialogButtons>
    </InvisibleForm>
  );
}

// TODO sam move this to base/ui?
export function DialogButtons({ children }: { children: React.ReactNode }) {
  return <div className={styles.dialogButtons}>{children}</div>;
}

const STRIPE_ELEMENTS_STYLE = {
  appearance: {
    variables: {
      fontFamily: "'Relative', system-ui, sans-serif",
      fontWeightBold: '500',
      colorPrimary: COLOR_TOKENS.colorPurple,
      colorPrimaryText: COLOR_TOKENS.colorWhite,
      colorBackground: COLOR_TOKENS.colorLightGrey3,
      colorDanger: COLOR_TOKENS.colorRed,
      colorDangerText: COLOR_TOKENS.colorWhite,
      colorSuccess: COLOR_TOKENS.colorGreen,
      colorSuccessText: COLOR_TOKENS.colorWhite,
      colorText: COLOR_TOKENS.colorBlack,
      colorTextSecondary: COLOR_TOKENS.colorGrey,
      colorTextPlaceholder: COLOR_TOKENS.colorGrey,
      borderRadius: '6px',
      focusOutline: '0',
      focusBoxShadow: '0',
      spacingGridRow: '16px',
      spacingGridColumn: '16px',
    },
    rules: {
      '.Label': {
        color: COLOR_TOKENS.colorGrey,
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: '400',
        marginBottom: '8px',
      },
      '.Input': {
        borderColor: COLOR_TOKENS.colorLightGrey3,
        backgroundColor: COLOR_TOKENS.colorLightGrey3,
        transition:
          'border-color 0.2s ease-out, background-color 0.2s ease-out',
        boxShadow: 'none',
        padding: '8px 10px',
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: '400',
      },
      '.Input:hover': {
        borderColor: COLOR_TOKENS.colorLightGrey2,
        backgroundColor: COLOR_TOKENS.colorLightGrey2,
      },
      '.Input:focus': {
        borderColor: COLOR_TOKENS.colorLightGrey3,
        backgroundColor: COLOR_TOKENS.colorWhite,
      },
      '.Input:hover:focus': {
        borderColor: COLOR_TOKENS.colorLightGrey3,
        backgroundColor: COLOR_TOKENS.colorWhite,
      },
      '.Input--invalid': {
        borderColor: COLOR_TOKENS.colorRed,
      },
    },
  },
  fonts: [
    {
      family: 'Relative',
      src: "url('https://www.relume.io/__assets/6177739448baa66404ce1d9c/6177746a5f7b0244353a13e6_Relative-Medium.ttf') format('truetype')",
      style: 'normal',
      weight: '500',
      display: 'swap',
    },
    {
      family: 'Relative',
      src: "url('https://www.relume.io/__assets/6177739448baa66404ce1d9c/6177746a17c7a083b39984ce_Relative-Book.ttf') format('truetype')",
      style: 'normal',
      weight: '400',
      display: 'swap',
    },
  ],
} satisfies Partial<StripeElementsOptions>;

function LoadingContainer({
  loading,
  children,
}: React.PropsWithChildren<{ loading: boolean }>) {
  return <div className={loading ? styles.loading : undefined}>{children}</div>;
}

function DummyContainer({ children }: React.PropsWithChildren) {
  return <>{children}</>;
}
